<template>
  <div>
    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100">品牌与功能</h1>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          管理租户品牌、查看已绑定域名，并在平台授予的范围内控制当前启用能力。
        </p>
      </div>

      <div v-if="preferredBrandHostUrl" class="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800/60">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div class="min-w-0 text-sm text-gray-600 dark:text-gray-300">
            <span class="text-gray-500 dark:text-gray-400">当前绑定域名：</span>
            <span class="font-semibold text-gray-900 dark:text-gray-100">{{ preferredBrandHost }}</span>
          </div>
          <a
            :href="preferredBrandHostUrl"
            target="_blank"
            rel="noreferrer"
            class="inline-flex shrink-0 items-center justify-center text-sm font-medium text-indigo-600 transition hover:text-indigo-700 dark:text-indigo-300 dark:hover:text-indigo-200"
          >
            访问站点
          </a>
        </div>
      </div>
    </div>

    <div class="rounded-lg bg-white shadow dark:bg-gray-800">
      <div class="px-4 pt-4">
        <Tabs :tabs="tabs" :active-tab="activeTab" @change="handleTabChange" />
      </div>

      <!-- Tab 1: 品牌设置 -->
      <div v-show="activeTab === 'brand'" class="p-6">
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-5">
          <!-- Left: Brand Form (3/5) -->
          <div class="lg:col-span-3">
            <form class="space-y-6" @submit.prevent="handleSaveBrand">
              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">站点名称</label>
                <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">用户访问网站时看到的名称，也会用于浏览器标题等页面名称。</p>
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <input
                    v-model="form.name"
                    type="text"
                    placeholder="请输入站点名称"
                    class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    :disabled="!authStore.isOwner || isSaving"
                  />
                </div>
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">品牌名称</label>
                <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">显示在 Logo 旁边的主品牌文字；可与站点名称不同。</p>
                <input
                  v-model="form.brandName"
                  type="text"
                  placeholder="请输入品牌名称"
                  class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  :disabled="!authStore.isOwner || isSaving"
                />
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">品牌别名</label>
                <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">显示在品牌名称下方的补充文字（比如英文等其他语言），可留空。</p>
                <input
                  v-model="form.brandNameAlias"
                  type="text"
                  placeholder="请输入品牌别名，可留空"
                  class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  :disabled="!authStore.isOwner || isSaving"
                />
              </div>

              <div class="flex items-center justify-between gap-3">
                <div>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ settings?.updatedAt ? '最后更新：' + formatDate(settings.updatedAt) : '尚未加载' }}
                  </span>
                </div>

                <button class="primary-btn" type="submit" :disabled="!authStore.isOwner || isSaving">
                  {{ isSaving ? '保存中...' : '保存品牌设置' }}
                </button>
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">浅色 Logo</label>
                <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  推荐用于白色或浅色背景下的方形品牌图标（如顶部导航栏）。<span class="font-medium text-amber-600 dark:text-amber-400">上传后可能需要几分钟才能在所有页面生效。</span>
                </p>
                <LogoUploader
                  variant="light"
                  :current-url="form.logoLightUrl"
                  @uploaded="handleLogoUploaded"
                />
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">深色 Logo</label>
                <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  推荐用于深色背景下的方形品牌图标。若未配置，将回退使用浅色 Logo。<span class="font-medium text-amber-600 dark:text-amber-400">上传后可能需要几分钟才能在所有页面生效。</span>
                </p>
                <LogoUploader
                  variant="dark"
                  :current-url="form.logoDarkUrl"
                  @uploaded="handleLogoUploaded"
                />
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">站点图标（Favicon）</label>
                <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  用于浏览器标签页与收藏夹图标。上传后会统一裁剪为 128x128 的 PNG，并覆盖当前租户托管图标。
                </p>
                <FaviconUploader
                  :current-url="form.faviconUrl"
                  @uploaded="handleFaviconUploaded"
                />
              </div>
            </form>
          </div>

          <!-- Right: Preview (2/5) -->
          <div class="lg:col-span-2">
            <div class="sticky top-6 space-y-4 lg:px-4 xl:px-8">
              <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">导航栏尺寸预览</h3>
              
              <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/80">
                <p class="mb-4 text-xs font-semibold tracking-wider text-gray-500 dark:text-gray-400 uppercase">浅色模式 (Light)</p>
                <div class="space-y-4">
                  <!-- Default -->
                  <div>
                    <p class="mb-1 text-[10px] text-gray-400">宽屏视口 (≥ 1100px)</p>
                    <div class="flex items-center gap-6 overflow-hidden rounded border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-600 dark:bg-white">
                      <BrandLockup preset="default" theme="light" :brand-name="form.brandName || form.name || 'Brand Name'" :brand-name-alias="form.brandNameAlias" :logo-light-url="previewLightLogo" />
                      <div class="hidden gap-6 text-sm font-medium text-gray-600 sm:flex"><span>首页</span><span>产品配置</span><span>用户管理</span></div>
                    </div>
                  </div>
                  <!-- Compact -->
                  <div>
                    <p class="mb-1 text-[10px] text-gray-400">紧凑视口 (960px - 1099px)</p>
                    <div class="flex items-center gap-4 overflow-hidden rounded border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-gray-600 dark:bg-white">
                      <BrandLockup preset="compact" theme="light" :brand-name="form.brandName || form.name || 'Brand Name'" :brand-name-alias="form.brandNameAlias" :logo-light-url="previewLightLogo" />
                      <div class="flex gap-4 text-sm font-medium text-gray-600"><span>首页</span><span>产品</span><span>用户</span></div>
                    </div>
                  </div>
                  <!-- Logo Only -->
                  <div>
                    <p class="mb-1 text-[10px] text-gray-400">极限压缩保护 (< 960px)</p>
                    <div class="flex items-center gap-3 overflow-hidden rounded border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-gray-600 dark:bg-white">
                      <BrandLockup preset="logo-only" theme="light" :brand-name="form.brandName || form.name || 'Brand Name'" :brand-name-alias="form.brandNameAlias" :logo-light-url="previewLightLogo" />
                      <div class="flex gap-3 text-sm font-medium text-gray-600"><span>首页</span><span>产品</span><span>用户</span></div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <p class="mb-4 text-xs font-semibold tracking-wider text-gray-400 uppercase">深色模式 (Dark)</p>
                <div class="space-y-4">
                  <!-- Default -->
                  <div>
                    <p class="mb-1 text-[10px] text-gray-500">宽屏视口 (≥ 1100px)</p>
                    <div class="flex items-center gap-6 overflow-hidden rounded border border-gray-700 bg-gray-800 px-4 py-3 shadow-sm">
                      <BrandLockup preset="default" theme="dark" :brand-name="form.brandName || form.name || 'Brand Name'" :brand-name-alias="form.brandNameAlias" :logo-dark-url="previewDarkLogo" />
                      <div class="hidden gap-6 text-sm font-medium text-gray-300 sm:flex"><span>首页</span><span>产品配置</span><span>用户管理</span></div>
                    </div>
                  </div>
                  <!-- Compact -->
                  <div>
                    <p class="mb-1 text-[10px] text-gray-500">紧凑视口 (960px - 1099px)</p>
                    <div class="flex items-center gap-4 overflow-hidden rounded border border-gray-700 bg-gray-800 px-3 py-2 shadow-sm">
                      <BrandLockup preset="compact" theme="dark" :brand-name="form.brandName || form.name || 'Brand Name'" :brand-name-alias="form.brandNameAlias" :logo-dark-url="previewDarkLogo" />
                      <div class="flex gap-4 text-sm font-medium text-gray-300"><span>首页</span><span>产品</span><span>用户</span></div>
                    </div>
                  </div>
                  <!-- Logo Only -->
                  <div>
                    <p class="mb-1 text-[10px] text-gray-500">极限压缩保护 (< 960px)</p>
                    <div class="flex items-center gap-3 overflow-hidden rounded border border-gray-700 bg-gray-800 px-3 py-2 shadow-sm">
                      <BrandLockup preset="logo-only" theme="dark" :brand-name="form.brandName || form.name || 'Brand Name'" :brand-name-alias="form.brandNameAlias" :logo-dark-url="previewDarkLogo" />
                      <div class="flex gap-3 text-sm font-medium text-gray-300"><span>首页</span><span>产品</span><span>用户</span></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab 2: 首页配置 -->
      <div v-show="activeTab === 'homepage'" class="p-6">
        <form class="max-w-4xl space-y-6" @submit.prevent="handleSaveHomepage">
          <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
            <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-100">首页配置</h3>
            <p class="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
              配置当前租户在主站根路径展示的首页内容。`URL` 模式会直接以租户页面为准，不再附加平台页脚。
            </p>
            <p v-if="isDemo" class="mt-2 text-xs text-amber-600 dark:text-amber-400">
              演示租户的首页修改仅临时生效，保存后会在 5 分钟后自动恢复为当前默认首页配置。
            </p>

            <div class="mt-4 space-y-4">
              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">首页模式</label>
                <select
                  v-model="form.homepageMode"
                  class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  :disabled="!authStore.isOwner || isSaving"
                >
                  <option value="default">使用平台默认首页</option>
                  <option value="url">外部 URL</option>
                  <option value="html">自定义 HTML</option>
                </select>
              </div>

              <div v-if="form.homepageMode !== 'default'">
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ form.homepageMode === 'url' ? '首页 URL' : '首页 HTML' }}
                </label>
                <textarea
                  v-model="form.homepageContent"
                  :rows="form.homepageMode === 'url' ? 4 : 12"
                  :placeholder="form.homepageMode === 'url' ? '请输入 https:// 开头的首页地址' : '请输入自定义首页 HTML（不允许 script / iframe / 内联事件）'"
                  class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  :disabled="!authStore.isOwner || isSaving"
                />
                <p class="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
                  <template v-if="form.homepageMode === 'url'">
                    仅支持 `https://` 地址；该模式下首页将完全由租户页面接管。
                  </template>
                  <template v-else>
                    HTML 模式不允许包含 `script`、内联事件、`javascript:` 协议以及 `iframe/object/embed`。
                  </template>
                </p>
              </div>

              <div class="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                  :disabled="!authStore.isOwner || isSaving"
                  @click="handlePreviewHomepage"
                >
                  预览首页
                </button>
                <span class="text-xs text-gray-500 dark:text-gray-400">
                  保存前可先本地预览当前草稿首页配置。
                </span>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
            <span class="text-xs text-gray-500 dark:text-gray-400">
              {{ settings?.updatedAt ? '最后更新：' + formatDate(settings.updatedAt) : '尚未加载' }}
            </span>
            <button class="primary-btn" type="submit" :disabled="!authStore.isOwner || isSaving">
              {{ isSaving ? '保存中...' : '保存首页配置' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Tab 3: 页脚备案 -->
      <div v-show="activeTab === 'filings'" class="p-6">
        <form class="max-w-4xl space-y-6" @submit.prevent="handleSaveFilings">
          <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
            <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-100">页脚备案</h3>
            <p class="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
              配置当前租户首页展示的备案信息。未配置则不展示；URL 首页模式下不会附加平台页脚。
            </p>
            <p v-if="isDemo" class="mt-2 text-xs text-amber-600 dark:text-amber-400">
              演示租户不支持修改页脚备案信息。
            </p>

            <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">ICP备案号</label>
                <input
                  v-model="form.footerIcpNumber"
                  type="text"
                  placeholder="例如：陕ICP备2025072629号"
                  class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  :disabled="!authStore.isOwner || isSaving"
                />
                <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">未配置则不展示。</p>
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">公安备案号</label>
                <input
                  v-model="form.footerPsbNumber"
                  type="text"
                  placeholder="例如：陕公网安备61011202001083号"
                  class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  :disabled="!authStore.isOwner || isSaving"
                />
                <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">未配置则不展示。</p>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
            <span class="text-xs text-gray-500 dark:text-gray-400">
              {{ settings?.updatedAt ? '最后更新：' + formatDate(settings.updatedAt) : '尚未加载' }}
            </span>
            <button class="primary-btn" type="submit" :disabled="!authStore.isOwner || isSaving">
              {{ isSaving ? '保存中...' : '保存页脚备案' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Tab 4: 在线客服 -->
      <div v-show="activeTab === 'support'" class="p-6">
        <form class="max-w-4xl space-y-6" @submit.prevent="handleSaveSupport">
              <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <div>
                  <div>
                    <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-100">在线客服</h3>
                    <p class="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                      配置当前租户在主站与教师端展示的客服入口。当前阶段仅支持微信对话平台，后续会扩展 Chatwoot、Crisp 等方案。
                    </p>
                  </div>
                </div>

            <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">客服类型</label>
                <select
                  v-model="form.supportProvider"
                  class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  :disabled="!authStore.isOwner || isSaving"
                >
                  <option value="none">使用平台客服</option>
                  <option value="wechat">微信对话平台</option>
                </select>
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">客服显示名称</label>
                <input
                  v-model="form.supportDisplayName"
                  type="text"
                  placeholder="留空时默认使用站点名称"
                  class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  :disabled="!authStore.isOwner || isSaving || form.supportProvider === 'none'"
                />
              </div>
            </div>

            <div v-if="form.supportProvider === 'none'" class="mt-4 space-y-3">
              <div class="rounded-lg border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-800/60 dark:text-gray-300">
                当前租户未单独配置客服，将自动回退到平台默认租户的在线客服配置。
              </div>

              <div class="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                  :disabled="isTestingSupport || !canPreviewSupport"
                  @click="handlePreviewSupport"
                >
                  {{ isTestingSupport ? '打开中...' : '测试打开平台客服' }}
                </button>
                <span class="text-xs text-gray-500 dark:text-gray-400">
                  测试入口将自动使用平台默认租户的客服配置。
                </span>
              </div>
            </div>

            <div v-else class="mt-4 space-y-3">
              <div>
                <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">微信对话平台 Token</label>
                  <a
                    href="https://developers.weixin.qq.com/doc/aispeech/platform/INTRODUCTION.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700 transition hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300 dark:hover:bg-sky-900/40"
                  >
                    查看配置文档
                  </a>
                </div>
                <input
                  v-model="form.supportWechatToken"
                  type="password"
                  autocomplete="new-password"
                  :placeholder="form.supportWechatConfigured ? '已配置 Token；留空表示保持不变' : '请输入微信对话平台 Token'"
                  class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  :disabled="!authStore.isOwner || isSaving"
                />
                <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  用于生成微信对话平台 Web H5 入口链接。{{ form.supportWechatConfigured ? '当前租户已存在 Token 配置。' : '当前租户尚未配置 Token。' }}
                </p>
              </div>

              <div class="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                  :disabled="isTestingSupport || !canPreviewSupport"
                  @click="handlePreviewSupport"
                >
                  {{ isTestingSupport ? '打开中...' : '测试打开客服' }}
                </button>
                <span class="text-xs text-gray-500 dark:text-gray-400">
                  测试入口将按当前租户配置打开访客模式客服窗口。
                </span>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
            <span class="text-xs text-gray-500 dark:text-gray-400">
              {{ settings?.updatedAt ? '最后更新：' + formatDate(settings.updatedAt) : '尚未加载' }}
            </span>
            <button class="primary-btn" type="submit" :disabled="!authStore.isOwner || isSaving">
              {{ isSaving ? '保存中...' : '保存在线客服设置' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Tab 4: 启用能力 -->
      <div v-show="activeTab === 'capabilities'" class="p-6">
        <form class="max-w-4xl space-y-6" @submit.prevent="handleSaveCapabilities">
          <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/50">
            <p class="text-sm text-gray-600 dark:text-gray-400">
              租户只能在平台授予的能力范围内勾选启用项。灰色选项表示平台未授予该能力。
            </p>
          </div>

          <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div>
              <h3 class="mb-4 text-base font-semibold text-gray-800 dark:text-gray-100">启用应用</h3>
              <div class="space-y-3">
                <label
                  v-for="option in APP_OPTIONS"
                  :key="option.value"
                  class="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300"
                >
                  <input
                    v-model="form.enabledApps"
                    :value="option.value"
                    type="checkbox"
                    :disabled="!authStore.isOwner || isSaving || !grantedAppSet.has(option.value)"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span :class="{ 'text-gray-400 dark:text-gray-600': !grantedAppSet.has(option.value) }">
                    {{ option.label }}
                  </span>
                </label>
              </div>
            </div>

            <div>
              <h3 class="mb-4 text-base font-semibold text-gray-800 dark:text-gray-100">启用登录方式</h3>
              <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
                从平台授予的登录方式中选择当前租户允许的登录方式。至少需要启用一种登录方式。
              </p>
              <div class="space-y-3">
                <label
                  v-for="option in LOGIN_METHOD_OPTIONS"
                  :key="option.value"
                  class="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300"
                >
                  <input
                    v-model="form.loginMethods"
                    :value="option.value"
                    type="checkbox"
                    :disabled="!authStore.isOwner || isSaving || !grantedLoginMethodSet.has(option.value)"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span :class="{ 'text-gray-400 dark:text-gray-600': !grantedLoginMethodSet.has(option.value) }">
                    {{ option.label }}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div class="flex justify-end border-t border-gray-200 pt-4 dark:border-gray-700">
            <button class="primary-btn" type="submit" :disabled="!authStore.isOwner || isSaving">
              {{ isSaving ? '保存中...' : '保存启用能力' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Tab 6: 域名信息 -->
      <div v-show="activeTab === 'domains'" class="p-6">
        <div class="max-w-4xl space-y-4">
          <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/50">
            <p class="text-sm text-gray-600 dark:text-gray-400">
              域名由平台统一维护，当前页面仅展示已绑定域名。如需修改域名配置，请联系平台管理员。
            </p>
          </div>

          <div>
            <h3 class="mb-3 text-base font-semibold text-gray-800 dark:text-gray-100">已绑定域名</h3>
            <div v-if="settings?.hosts.length" class="space-y-3">
              <div
                v-for="host in settings?.hosts || []"
                :key="host.id"
                class="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700 dark:bg-gray-800/60"
              >
                <div>
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ host.host }}</p>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ hostUrlMap[host.id] }}</p>
                </div>

                <a
                  :href="hostUrlMap[host.id]"
                  target="_blank"
                  rel="noreferrer"
                  class="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-indigo-200 hover:text-indigo-600 dark:border-gray-600 dark:text-gray-200 dark:hover:text-indigo-300"
                >
                  访问站点
                </a>
              </div>
            </div>
            <p v-else class="text-sm text-gray-500 dark:text-gray-400">平台暂未配置域名。</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";

import { getSettingsAPI, getSupportLinkPreviewAPI, updateSettingsAPI } from "@/api/tenantConsole";
import FaviconUploader from "@/components/FaviconUploader.vue";
import LogoUploader from "@/components/LogoUploader.vue";
import BrandLockup from "@/components/branding/BrandLockup.vue";
import { useAuthStore } from "@/stores/auth";
import { APP_OPTIONS, LOGIN_METHOD_OPTIONS, type TenantFaviconUploadResponse, type TenantHomepageMode, type TenantSettings, type TenantLogoUploadResponse } from "@/types/tenantConsole";
import Tabs from "@/components/common/Tabs.vue";
import toaster from "@/utils/toaster";

const authStore = useAuthStore();
const isDemo = computed(() => authStore.isDemo);
const settings = ref<TenantSettings | null>(null);
const isSaving = ref(false);
const activeTab = ref('brand');

const HOMEPAGE_SCRIPT_PATTERN = /<\s*script\b/i
const HOMEPAGE_INLINE_EVENT_PATTERN = /on[a-z]+\s*=/i
const HOMEPAGE_JAVASCRIPT_PROTOCOL_PATTERN = /javascript\s*:/i
const HOMEPAGE_EMBEDDED_FRAME_PATTERN = /<\s*(iframe|object|embed)\b/i
const HOMEPAGE_META_REFRESH_PATTERN = /<meta\b[^>]*http-equiv\s*=\s*['"]?refresh/i

const tabs = [
  { id: 'brand', label: '品牌设置' },
  { id: 'homepage', label: '首页配置' },
  { id: 'filings', label: '页脚备案' },
  { id: 'support', label: '在线客服' },
  { id: 'capabilities', label: '启用能力' },
  { id: 'domains', label: '域名信息' },
];

const form = reactive({
  name: "",
  brandName: "",
  brandNameAlias: "",
  logoLightUrl: "",
  logoDarkUrl: "",
  faviconUrl: "",
  homepageMode: "default" as TenantHomepageMode,
  homepageContent: "",
  footerIcpNumber: "",
  footerPsbNumber: "",
  supportProvider: "none" as "none" | "wechat",
  supportDisplayName: "",
  supportWechatConfigured: false,
  supportWechatToken: "",
  enabledApps: [] as string[],
  loginMethods: [] as string[],
});

const lightPreviewUrl = ref("");
const darkPreviewUrl = ref("");
const isTestingSupport = ref(false);

const previewLightLogo = computed(() => lightPreviewUrl.value || form.logoLightUrl);
const previewDarkLogo = computed(() => darkPreviewUrl.value || form.logoDarkUrl || form.logoLightUrl);
const preferredBrandHost = computed(() => {
  const hosts = settings.value?.hosts || []
  if (!hosts.length) {
    return ""
  }

  return (hosts.find((host) => !host.host.endsWith('.magly.cn')) || hosts[0])?.host || ""
})
const preferredBrandHostUrl = computed(() => {
  if (!preferredBrandHost.value) {
    return ""
  }

  return `https://${preferredBrandHost.value}`
})
const canPreviewSupport = computed(() => {
  if (form.supportProvider === 'none') {
    return true;
  }

  return Boolean(form.supportWechatToken.trim() || form.supportWechatConfigured);
});
const appLabelMap = Object.fromEntries(APP_OPTIONS.map((option) => [option.value, option.label]));
const loginMethodLabelMap = Object.fromEntries(LOGIN_METHOD_OPTIONS.map((option) => [option.value, option.label]));
const grantedAppOptions = computed(() => settings.value?.grantedApps || []);
const grantedLoginMethodOptions = computed(() => settings.value?.grantedLoginMethods || []);
const grantedAppSet = computed(() => new Set(grantedAppOptions.value));
const grantedLoginMethodSet = computed(() => new Set(grantedLoginMethodOptions.value));

const formatDate = (dateTime: string) => new Date(dateTime).toLocaleString("zh-CN");
const hostUrlMap = computed<Record<number, string>>(() => {
  return Object.fromEntries(
    (settings.value?.hosts || []).map((host) => [host.id, `https://${host.host}`]),
  )
})

/**
 * 校验首页配置。
 */
const validateHomepageConfig = (homepageMode: TenantHomepageMode, homepageContent: string) => {
  const normalizedContent = homepageContent.trim()

  if (homepageMode === 'default') {
    return { valid: true, content: '', error: null as string | null }
  }

  if (!normalizedContent) {
    return { valid: false, content: '', error: '自定义主页内容不能为空' }
  }

  if (homepageMode === 'url') {
    if (!normalizedContent.startsWith('https://')) {
      return { valid: false, content: '', error: 'URL 模式仅支持 https 地址' }
    }

    return { valid: true, content: normalizedContent, error: null as string | null }
  }

  if (HOMEPAGE_SCRIPT_PATTERN.test(normalizedContent)) {
    return { valid: false, content: '', error: 'HTML 模式不允许包含 script 脚本' }
  }

  if (HOMEPAGE_INLINE_EVENT_PATTERN.test(normalizedContent)) {
    return { valid: false, content: '', error: 'HTML 模式不允许包含内联事件处理器' }
  }

  if (HOMEPAGE_JAVASCRIPT_PROTOCOL_PATTERN.test(normalizedContent)) {
    return { valid: false, content: '', error: 'HTML 模式不允许使用 javascript: 协议' }
  }

  if (HOMEPAGE_EMBEDDED_FRAME_PATTERN.test(normalizedContent)) {
    return { valid: false, content: '', error: 'HTML 模式不允许嵌套 iframe、object 或 embed' }
  }

  if (HOMEPAGE_META_REFRESH_PATTERN.test(normalizedContent)) {
    return { valid: false, content: '', error: 'HTML 模式不允许使用 meta refresh' }
  }

  return { valid: true, content: normalizedContent, error: null as string | null }
}

/**
 * 生成 HTML 模式预览文档。
 */
const buildHomepagePreviewDocument = (htmlContent: string) => `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>首页预览</title>
  <style>
    html, body { margin: 0; padding: 0; height: 100%; background: #f3f4f6; }
    iframe { width: 100%; min-height: 100vh; border: 0; background: #fff; }
  </style>
</head>
<body>
  <iframe sandbox="allow-forms allow-popups allow-popups-to-escape-sandbox" referrerpolicy="no-referrer" srcdoc=${JSON.stringify(htmlContent)}></iframe>
</body>
</html>`

const handleTabChange = (tabId: string) => {
  activeTab.value = tabId;
};

/**
 * 处理 Logo 上传成功。
 */
const handleLogoUploaded = (payload: TenantLogoUploadResponse) => {
  if (payload.variant === 'light') {
    form.logoLightUrl = payload.logoUrl
    lightPreviewUrl.value = payload.previewUrl || ''
  } else {
    form.logoDarkUrl = payload.logoUrl
    darkPreviewUrl.value = payload.previewUrl || ''
  }
}

/**
 * 处理 Favicon 上传成功。
 */
const handleFaviconUploaded = (payload: TenantFaviconUploadResponse) => {
  form.faviconUrl = payload.faviconUrl
}

/**
 * 测试打开在线客服。
 */
const handlePreviewSupport = async () => {
  if (isTestingSupport.value || !canPreviewSupport.value) {
    return
  }

  const pendingWindow = window.open('', '_blank')
  if (!pendingWindow) {
    toaster.error('浏览器拦截了新窗口，请允许弹窗后重试')
    return
  }

  pendingWindow.opener = null
  pendingWindow.document.title = '正在打开客服...'
  pendingWindow.document.body.innerHTML = '<div style="font-family: -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif; padding: 24px; color: #374151;">正在打开客服...</div>'

  isTestingSupport.value = true
  try {
    const response = await getSupportLinkPreviewAPI({
      supportProvider: form.supportProvider,
      supportDisplayName: form.supportDisplayName.trim(),
      supportWechatToken: form.supportWechatToken.trim() || undefined,
    })
    if (response.status !== 200 || !response.data?.url) {
      pendingWindow.close()
      toaster.error(response.message || '获取客服链接失败')
      return
    }

    pendingWindow.location.href = response.data.url
  } catch {
    pendingWindow.close()
    toaster.error('获取客服链接失败')
  } finally {
    isTestingSupport.value = false
  }
}

/**
 * 预览首页配置。
 */
const handlePreviewHomepage = () => {
  const validation = validateHomepageConfig(form.homepageMode, form.homepageContent)
  if (!validation.valid) {
    toaster.error(validation.error || '主页配置无效')
    return
  }

  if (form.homepageMode === 'default') {
    toaster.info('默认首页无需预览')
    return
  }

  const previewWindow = window.open('', '_blank')
  if (!previewWindow) {
    toaster.error('浏览器拦截了预览窗口，请允许弹窗后重试')
    return
  }

  previewWindow.opener = null

  if (form.homepageMode === 'url') {
    previewWindow.location.href = validation.content
    return
  }

  previewWindow.document.open()
  previewWindow.document.write(buildHomepagePreviewDocument(validation.content))
  previewWindow.document.close()
}

/**
 * 同步表单数据。
 */
const syncForm = (nextSettings: TenantSettings) => {
  lightPreviewUrl.value = "";
  darkPreviewUrl.value = "";
  form.name = nextSettings.name;
  form.brandName = nextSettings.brandName;
  form.brandNameAlias = nextSettings.brandNameAlias;
  form.logoLightUrl = nextSettings.logoLightUrl;
  form.logoDarkUrl = nextSettings.logoDarkUrl;
  form.faviconUrl = nextSettings.faviconUrl;
  form.homepageMode = nextSettings.homepageMode;
  form.homepageContent = nextSettings.homepageContent;
  form.footerIcpNumber = nextSettings.footerIcpNumber;
  form.footerPsbNumber = nextSettings.footerPsbNumber;
  form.supportProvider = nextSettings.supportProvider;
  form.supportDisplayName = nextSettings.supportDisplayName;
  form.supportWechatConfigured = nextSettings.supportWechatConfigured;
  form.supportWechatToken = "";
  form.enabledApps = [...nextSettings.enabledApps].filter((item) => nextSettings.grantedApps.includes(item));
  form.loginMethods = [...nextSettings.loginMethods].filter((item) => nextSettings.grantedLoginMethods.includes(item));
};

/**
 * 保存首页配置。
 */
const handleSaveHomepage = async () => {
  if (!authStore.isOwner || isSaving.value) {
    return;
  }

  const homepageValidation = validateHomepageConfig(form.homepageMode, form.homepageContent)
  if (!homepageValidation.valid) {
    toaster.error(homepageValidation.error || '主页配置无效')
    return
  }

  isSaving.value = true;

  try {
    const response = await updateSettingsAPI({
      homepageMode: form.homepageMode,
      homepageContent: homepageValidation.content,
    });

    if (response.status !== 200 || !response.data) {
      toaster.error(response.message || '保存首页配置失败');
      return;
    }

    settings.value = response.data;
    syncForm(response.data);
    toaster.success('首页配置已更新');
  } catch {
    toaster.error('保存首页配置失败');
  } finally {
    isSaving.value = false;
  }
};

/**
 * 加载租户设置。
 */
const loadSettings = async () => {
  const response = await getSettingsAPI();
  if (response.status !== 200 || !response.data) {
    toaster.error(response.message || "加载租户设置失败");
    return;
  }

  settings.value = response.data;
  syncForm(response.data);
};

/**
 * 保存品牌设置。
 */
const handleSaveBrand = async () => {
  if (!authStore.isOwner || isSaving.value) {
    return;
  }

  isSaving.value = true;

  try {
    const response = await updateSettingsAPI({
      name: form.name.trim(),
      brandName: form.brandName.trim(),
      brandNameAlias: form.brandNameAlias.trim(),
    });

    if (response.status !== 200 || !response.data) {
      toaster.error(response.message || "保存品牌设置失败");
      return;
    }

    settings.value = response.data;
    syncForm(response.data);
    if (authStore.account && authStore.tenant) {
      authStore.setAuthContext(authStore.account, {
        ...authStore.tenant,
        name: response.data.name,
        brandName: response.data.brandName,
        brandNameAlias: response.data.brandNameAlias,
        logoLightUrl: response.data.logoLightUrl,
        logoDarkUrl: response.data.logoDarkUrl,
        faviconUrl: response.data.faviconUrl,
        grantedApps: response.data.grantedApps,
        enabledApps: response.data.enabledApps,
        grantedLoginMethods: response.data.grantedLoginMethods,
        loginMethods: response.data.loginMethods,
        isActive: response.data.isActive,
      });
    }

    toaster.success("品牌设置已更新");
  } catch {
    toaster.error("保存品牌设置失败");
  } finally {
    isSaving.value = false;
  }
};

/**
 * 保存页脚备案配置。
 */
const handleSaveFilings = async () => {
  if (!authStore.isOwner || isSaving.value) {
    return;
  }

  if (isDemo.value) {
    toaster.error('演示租户不支持修改页脚备案信息');
    return;
  }

  isSaving.value = true;

  try {
    const response = await updateSettingsAPI({
      footerIcpNumber: form.footerIcpNumber.trim(),
      footerPsbNumber: form.footerPsbNumber.trim(),
    });

    if (response.status !== 200 || !response.data) {
      toaster.error('保存页脚备案失败');
      return;
    }

    settings.value = response.data;
    syncForm(response.data);
    toaster.success('页脚备案已更新');
  } catch {
    toaster.error('保存页脚备案失败');
  } finally {
    isSaving.value = false;
  }
};

/**
 * 保存在线客服设置。
 */
const handleSaveSupport = async () => {
  if (!authStore.isOwner || isSaving.value) {
    return;
  }

  isSaving.value = true;

  try {
    const response = await updateSettingsAPI({
      supportProvider: form.supportProvider,
      supportDisplayName: form.supportDisplayName.trim(),
      supportWechatToken: form.supportWechatToken.trim() || undefined,
      enabledApps: form.enabledApps,
      loginMethods: form.loginMethods,
    });

    if (response.status !== 200 || !response.data) {
      toaster.error(response.message || '保存在线客服设置失败');
      return;
    }

    settings.value = response.data;
    syncForm(response.data);
    if (authStore.account && authStore.tenant) {
      authStore.setAuthContext(authStore.account, {
        ...authStore.tenant,
        name: response.data.name,
        brandName: response.data.brandName,
        brandNameAlias: response.data.brandNameAlias,
        logoLightUrl: response.data.logoLightUrl,
        logoDarkUrl: response.data.logoDarkUrl,
        faviconUrl: response.data.faviconUrl,
        grantedApps: response.data.grantedApps,
        enabledApps: response.data.enabledApps,
        grantedLoginMethods: response.data.grantedLoginMethods,
        loginMethods: response.data.loginMethods,
        isActive: response.data.isActive,
      });
    }

    toaster.success('在线客服设置已更新');
  } catch {
    toaster.error('保存在线客服设置失败');
  } finally {
    isSaving.value = false;
  }
};

/**
 * 保存启用能力。
 */
const handleSaveCapabilities = async () => {
  if (!authStore.isOwner || isSaving.value) {
    return;
  }

  if (form.loginMethods.length === 0) {
    toaster.error("至少需要启用一种登录方式");
    return;
  }

  isSaving.value = true;

  try {
    const response = await updateSettingsAPI({
      name: form.name.trim(),
      logoLightUrl: form.logoLightUrl.trim(),
      logoDarkUrl: form.logoDarkUrl.trim(),
      enabledApps: form.enabledApps,
      loginMethods: form.loginMethods,
    });

    if (response.status !== 200 || !response.data) {
      toaster.error(response.message || "保存启用能力失败");
      return;
    }

    settings.value = response.data;
    syncForm(response.data);
    if (authStore.account && authStore.tenant) {
      authStore.setAuthContext(authStore.account, {
        ...authStore.tenant,
        name: response.data.name,
        brandName: response.data.brandName,
        brandNameAlias: response.data.brandNameAlias,
        logoLightUrl: response.data.logoLightUrl,
        logoDarkUrl: response.data.logoDarkUrl,
        faviconUrl: response.data.faviconUrl,
        grantedApps: response.data.grantedApps,
        enabledApps: response.data.enabledApps,
        grantedLoginMethods: response.data.grantedLoginMethods,
        loginMethods: response.data.loginMethods,
        isActive: response.data.isActive,
      });
    }

    toaster.success("启用能力已更新");
  } catch {
    toaster.error("保存启用能力失败");
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  loadSettings();
});
</script>
